import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useSeguroRuta from "../../hook/useSeguroRuta";
import { ListarProcedimientoConPrecios } from "../../Service/Service.examen";
import { ListCategoriaExamen } from "../../Service/Service.categoriaExamen";
import { ListadoPlanes, PostNewPlan, PutUpdatePlan } from "../../Service/Service.Plan";
import { usePageSubtitulo } from "../../context/PageHeaderContext";
import { tituloClinico } from "../../utils/textoClinico";
import { formatearPrecio } from "../../utils/tarifarios";
import Boton from "../../Components/UI/Boton";
import ModalKit from "../../Components/UI/Modal/Modal";
import { Aviso, useToast } from "../../Components/UI/Aviso";
import { ChipEstado } from "../../Components/UI/Chip";
import { Campo, CampoTexto, CampoSelect, Casilla } from "../../Components/UI/Campo";
import {
    IconoChevronIzquierda,
    IconoChevronAbajo,
    IconoChevronDerecha,
    IconoBuscar,
    IconoEliminar,
} from "../../Components/UI/Iconos";
import {
    idFila,
    idExamen,
    examenesOcupados,
    bloqueadaPor,
    filtrarCatalogo,
    agregarTodas,
    quitarTodas,
    cuantasPuestas,
    copagosInvalidos,
    resumenPlan,
    construirDetalle,
    construirEliminar,
    hayCambios,
} from "./planConstructorLogica";
import { claveBorrador, leerBorrador, borrarBorrador, useGuardadoBorrador } from "./usePlanBorrador";
import Pagina from "./Pagina";
import "../../styles/Seguros.css";

/* ============================================================================
   SEGUROS-PLANES FASE 4 · el constructor de plan.

   Es la pantalla que resuelve el problema. Hoy armar un plan es un modal con
   490 procedimientos en 49 páginas de 10, donde marcar todo "Laboratorio" es
   materialmente imposible y el nombre del plan se pide al final.

   Las cuatro decisiones que lo cambian todo:

   1. ES UNA PÁGINA, NO UN MODAL. La tarea dura minutos: merece la pantalla
      entera, su URL, el botón Atrás y poder recargar. Y un borrador, para que
      cerrar por accidente no cueste el trabajo.

   2. EL CATÁLOGO SE CARGA ENTERO, UNA VEZ, SIN PAGINAR. El tarifario más
      grande de una aseguradora son 1.030 filas / 474 KB (medido, informe FASE
      0 §4). Cabe. Y al caber, buscar y filtrar son instantáneos, "Agregar los
      127" es filtrar un array, y el problema de la selección perdida
      desaparece por construcción -- no hay páginas que perder.

   3. LA SELECCIÓN VIVE POR ID, EN UN Map. Nunca en un array atado a lo que se
      ve. Filtrar, buscar o desplazarse no puede tocarla: sólo la tocan las
      acciones del usuario. Ese era el bug.

   4. EL NOMBRE VA PRIMERO, Y CON EL FOCO PUESTO. Es lo que da sentido al
      trabajo.

   Lo que NO hace: no cambia ninguna regla de negocio, no toca ningún endpoint
   y no añade ni quita una sola validación. Un plan creado aquí produce las
   mismas filas que uno creado con el modal viejo.
   ========================================================================== */

const SIN_CATEGORIA = "";

function Fila({ fila, marcada, bloqueada, onCambio }) {
    const desc = tituloClinico(fila.ExamenTipo?.descripcion || "");
    const codigo = fila.ExamenTipo?.codigo || "";
    return (
        <div
            className={`pc-fila${marcada ? " pc-fila--puesta" : ""}${bloqueada ? " pc-fila--bloqueada" : ""}`}
            data-testid={`pc-fila-${idFila(fila)}`}
        >
            <Casilla
                marcada={marcada}
                disabled={bloqueada}
                onChange={(v) => onCambio(fila, v)}
                etiqueta={desc}
            >
                <span className="pc-fila__texto">
                    <span className="pc-fila__desc" title={desc}>
                        {desc}
                    </span>
                    <span className="pc-fila__codigo">{codigo}</span>
                </span>
                <span className="pc-fila__precio">{formatearPrecio(fila.precio)}</span>
            </Casilla>
        </div>
    );
}

export default function PlanConstructor() {
    const navigate = useNavigate();
    const { idplan } = useParams();
    const [params] = useSearchParams();
    const idDuplicar = params.get("duplicar");
    const { idseguro, seguro, cargando: cargandoSeguro, error: errorSeguro, recargar } = useSeguroRuta();
    const { mostrar } = useToast();

    const edicion = Boolean(idplan);

    // ---------------------------------------------------------------- estado
    const [nombre, setNombre] = useState("");
    const [copagoDefecto, setCopagoDefecto] = useState("0");
    // LA SELECCIÓN: Map<idprocedimiento_company_precios, copago como texto>.
    // Texto, no número, porque el usuario tiene que poder dejar el campo vacío
    // mientras teclea sin que se convierta en un 0 a su espalda.
    const [puestos, setPuestos] = useState(() => new Map());
    const [marcados, setMarcados] = useState(() => new Set()); // para la barra en lote
    const [plegados, setPlegados] = useState(() => new Set());

    const [catalogo, setCatalogo] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [extras, setExtras] = useState([]); // filas del plan que ya no están en el catálogo
    const [original, setOriginal] = useState(null);
    const [idsOriginales, setIdsOriginales] = useState([]);

    const [cargando, setCargando] = useState(true);
    const [errorCarga, setErrorCarga] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [errorGuardado, setErrorGuardado] = useState(null);
    const [confirmar, setConfirmar] = useState(null); // "vaciar" | "salir"
    const [borradorRecuperado, setBorradorRecuperado] = useState(false);

    const [busquedaInput, setBusquedaInput] = useState("");
    const [busqueda, setBusqueda] = useState("");
    const [categoria, setCategoria] = useState(SIN_CATEGORIA);

    const [planesDelSeguro, setPlanesDelSeguro] = useState([]);
    const [duplicando, setDuplicando] = useState(false);

    // `CampoTexto` no reenvia `ref` (no es forwardRef), asi que el foco se
    // consigue con `autoFocus` al montar y con el id para devolverlo cuando una
    // validacion falla. Es la via que ofrece el kit, sin tocarlo.
    const ID_NOMBRE = "pc-nombre-campo";
    const enfocarNombre = () => document.getElementById(ID_NOMBRE)?.focus();
    const clave = claveBorrador(idseguro, idplan);

    // El copago por defecto se lee por REFERENCIA dentro de `alternarFila`: si
    // fuera una dependencia, cambiarlo reconstruiria el callback y con el las
    // 1.030 filas del catalogo en cada pulsacion.
    const copagoDefectoRef = useRef(copagoDefecto);
    useEffect(() => {
        copagoDefectoRef.current = copagoDefecto;
    }, [copagoDefecto]);

    // ------------------------------------------------------------ carga
    // Todas las filas conocidas, indexadas por su id de tarifario. Incluye las
    // `extras`: en un plan histórico puede haber líneas cuyo procedimiento ya
    // no cumple el filtro del catálogo (dado de baja, o su ExamenTipo
    // inactivo). Si no las tuviéramos, al guardar desaparecerían en silencio
    // -- que es modificar datos existentes sin que nadie lo haya pedido.
    const filasPorId = useMemo(() => {
        const mapa = new Map();
        for (const f of catalogo) mapa.set(idFila(f), f);
        for (const f of extras) if (!mapa.has(idFila(f))) mapa.set(idFila(f), f);
        return mapa;
    }, [catalogo, extras]);

    const cargar = useCallback(async () => {
        if (!idseguro) return;
        setCargando(true);
        setErrorCarga(null);
        try {
            // El tarifario COMPLETO de la aseguradora, sin page/limit. Una sola
            // petición para toda la sesión de trabajo.
            const [respTarifario, respCategorias] = await Promise.all([
                ListarProcedimientoConPrecios({ seguro_id: idseguro }),
                ListCategoriaExamen(),
            ]);

            if (!respTarifario?.success && !Array.isArray(respTarifario?.data)) {
                throw new Error("tarifario");
            }
            setCatalogo(respTarifario.data || []);
            setCategorias(
                (respCategorias?.data || []).map((c) => ({
                    value: String(c.IdCategoriaExamen),
                    label: tituloClinico(c.Descripcion),
                }))
            );

            // Los planes de esta aseguradora sirven para dos cosas: el atajo
            // "Duplicar uno existente" y, en edición, para cargar el plan. Una
            // sola petición cubre ambas -- el endpoint ya devuelve los detalles.
            let planes = [];
            if (edicion || idDuplicar) {
                const resp = await ListadoPlanes({ seguro_id: idseguro, estado: "Todos" });
                planes = resp?.data || [];
                setPlanesDelSeguro(planes);
            }

            const fuente = edicion
                ? planes.find((p) => String(p.id_plan) === String(idplan))
                : idDuplicar
                ? planes.find((p) => String(p.id_plan) === String(idDuplicar))
                : null;

            if (edicion && !fuente) throw new Error("plan");

            if (fuente) {
                const mapa = new Map();
                const fueraDeCatalogo = [];
                for (const d of fuente.detalles || []) {
                    const id = Number(d.idprocedimiento_company_precios);
                    mapa.set(id, String(Number(d.copago)));
                    fueraDeCatalogo.push({
                        idprocedimiento_company_precios: id,
                        idExamenTipo: Number(d.id_tipo_examen),
                        precio: d.tarifario?.precio ?? 0,
                        ExamenTipo: d.ExamenTipo,
                    });
                }
                setExtras(fueraDeCatalogo);
                setPuestos(mapa);
                if (edicion) {
                    setNombre(fuente.nombre || "");
                    setOriginal({ nombre: fuente.nombre || "", puestos: new Map(mapa) });
                    setIdsOriginales([...mapa.keys()]);
                } else {
                    // Duplicar: se copian líneas y copagos, el nombre va vacío.
                    setNombre("");
                }
            }

            // El borrador manda sobre lo cargado: es trabajo del usuario que
            // todavía no llegó al servidor.
            const borrador = leerBorrador(clave);
            if (borrador) {
                setNombre(borrador.nombre);
                setPuestos(new Map(borrador.puestos));
                setBorradorRecuperado(true);
            }
        } catch (e) {
            setErrorCarga(
                e.message === "plan"
                    ? "Este plan no existe o no pertenece a esta aseguradora."
                    : "No se pudo cargar el tarifario de la aseguradora."
            );
        } finally {
            setCargando(false);
        }
    }, [idseguro, edicion, idplan, idDuplicar, clave]);

    useEffect(() => {
        cargar();
    }, [cargar]);



    // La búsqueda es local (el catálogo está entero en memoria), pero se espera
    // una pausa igualmente: filtrar 1.030 filas en cada tecla se nota.
    useEffect(() => {
        const t = setTimeout(() => setBusqueda(busquedaInput), 120);
        return () => clearTimeout(t);
    }, [busquedaInput]);

    const datosBorrador = useMemo(
        () => ({ nombre, puestos: [...puestos.entries()] }),
        [nombre, puestos]
    );
    const borradorGuardado = useGuardadoBorrador(clave, datosBorrador, !cargando && !errorCarga);

    // ------------------------------------------------------------ derivados
    const ocupados = useMemo(() => examenesOcupados(puestos, filasPorId), [puestos, filasPorId]);
    const filtradas = useMemo(
        () => filtrarCatalogo(catalogo, { texto: busqueda, categoria }),
        [catalogo, busqueda, categoria]
    );
    const yaPuestasDelFiltro = useMemo(
        () => cuantasPuestas(puestos, filtradas),
        [puestos, filtradas]
    );
    const resumen = useMemo(() => resumenPlan(puestos, filasPorId), [puestos, filasPorId]);
    const invalidos = useMemo(() => copagosInvalidos(puestos), [puestos]);

    const cambios = useMemo(() => hayCambios(original, { nombre, puestos }), [original, nombre, puestos]);

    const nombreCategoria = useMemo(() => {
        if (categoria === SIN_CATEGORIA) return null;
        return categorias.find((c) => c.value === String(categoria))?.label || null;
    }, [categoria, categorias]);

    // Panel derecho: las líneas del plan agrupadas por categoría.
    const grupos = useMemo(() => {
        const porCategoria = new Map();
        for (const id of puestos.keys()) {
            const fila = filasPorId.get(id);
            const catId = fila?.ExamenTipo?.categoria_id ?? "otras";
            const etiqueta =
                tituloClinico(fila?.ExamenTipo?.categoria?.Descripcion || "") || "Sin categoría";
            if (!porCategoria.has(catId)) porCategoria.set(catId, { catId, etiqueta, filas: [] });
            porCategoria.get(catId).filas.push({ id, fila });
        }
        return [...porCategoria.values()].sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, "es"));
    }, [puestos, filasPorId]);

    // ------------------------------------------------------------- acciones
    const alternarFila = useCallback((fila, dentro) => {
        const id = idFila(fila);
        setPuestos((prev) => {
            const siguiente = new Map(prev);
            if (dentro) siguiente.set(id, String(Number(copagoDefectoRef.current) || 0));
            else siguiente.delete(id);
            return siguiente;
        });
        if (!dentro) {
            setMarcados((prev) => {
                const s = new Set(prev);
                s.delete(id);
                return s;
            });
        }
    }, []);

    const agregarFiltro = () => {
        const r = agregarTodas(puestos, filtradas, String(Number(copagoDefecto) || 0), filasPorId);
        setPuestos(r.puestos);
        if (r.saltadas > 0) {
            mostrar({
                tipo: "aviso",
                texto: `Se agregaron ${r.agregadas}. ${r.saltadas} se omitieron porque el plan ya incluye ese mismo examen con otro precio.`,
            });
        }
    };

    const quitarFiltro = () => {
        const r = quitarTodas(puestos, filtradas);
        setPuestos(r.puestos);
        setMarcados(new Set());
    };

    const ponerCopago = (id, valor) =>
        setPuestos((prev) => {
            const s = new Map(prev);
            s.set(id, valor);
            return s;
        });

    const copagoDeGrupo = (grupo, valor) =>
        setPuestos((prev) => {
            const s = new Map(prev);
            for (const { id } of grupo.filas) s.set(id, valor);
            return s;
        });

    const copagoEnLote = (valor) =>
        setPuestos((prev) => {
            const s = new Map(prev);
            for (const id of marcados) if (s.has(id)) s.set(id, valor);
            return s;
        });

    const quitarEnLote = () => {
        setPuestos((prev) => {
            const s = new Map(prev);
            for (const id of marcados) s.delete(id);
            return s;
        });
        setMarcados(new Set());
    };

    const quitarUna = (id) => {
        setPuestos((prev) => {
            const s = new Map(prev);
            s.delete(id);
            return s;
        });
        setMarcados((prev) => {
            const s = new Set(prev);
            s.delete(id);
            return s;
        });
    };

    const vaciar = () => {
        setPuestos(new Map());
        setMarcados(new Set());
        setConfirmar(null);
    };

    const duplicarDe = async (idOrigen) => {
        if (!idOrigen) return;
        setDuplicando(true);
        const origen = planesDelSeguro.find((p) => String(p.id_plan) === String(idOrigen));
        if (origen) aplicarPlan(origen);
        setDuplicando(false);
    };

    const aplicarPlan = (plan) => {
        const mapa = new Map();
        const nuevasExtras = [];
        for (const d of plan.detalles || []) {
            const id = Number(d.idprocedimiento_company_precios);
            mapa.set(id, String(Number(d.copago)));
            nuevasExtras.push({
                idprocedimiento_company_precios: id,
                idExamenTipo: Number(d.id_tipo_examen),
                precio: d.tarifario?.precio ?? 0,
                ExamenTipo: d.ExamenTipo,
            });
        }
        setExtras((prev) => [...prev, ...nuevasExtras]);
        setPuestos(mapa);
    };

    // Para el desplegable de duplicar hace falta la lista aunque no vengamos de
    // "Duplicar": se pide sólo cuando se abre el atajo.
    const cargarPlanesSiHaceFalta = async () => {
        if (planesDelSeguro.length > 0) return;
        const resp = await ListadoPlanes({ seguro_id: idseguro, estado: "Todos" });
        setPlanesDelSeguro(resp?.data || []);
    };

    const volver = () => navigate(`/configuracion/seguros/${idseguro}/planes`);

    const guardar = async () => {
        setErrorGuardado(null);

        // Las validaciones son EXACTAMENTE las de la pantalla vieja.
        if (!nombre.trim()) {
            mostrar({ tipo: "aviso", texto: "El plan necesita un nombre." });
            enfocarNombre();
            return;
        }
        if (puestos.size === 0) {
            mostrar({ tipo: "aviso", texto: "El plan necesita al menos un procedimiento." });
            return;
        }
        if (invalidos.length > 0) {
            mostrar({
                tipo: "aviso",
                texto: "Todos los procedimientos seleccionados deben tener un copago válido.",
            });
            return;
        }

        setGuardando(true);
        try {
            const cabecera = { nombre: nombre.trim(), seguro_id: idseguro };
            const detalle = construirDetalle(puestos, filasPorId);

            const respuesta = edicion
                ? await PutUpdatePlan(Number(idplan), {
                      cabecera,
                      detalle,
                      eliminar: construirEliminar(idsOriginales, puestos),
                  })
                : await PostNewPlan({ cabecera, detalle });

            if (!respuesta?.success) throw new Error(respuesta?.message || "No se pudo guardar el plan.");

            borrarBorrador(clave);
            mostrar({
                tipo: "exito",
                texto: respuesta.message || (edicion ? "Plan actualizado" : "Plan creado"),
            });
            volver();
        } catch (e) {
            // El trabajo NO se pierde: se queda todo en pantalla y se ofrece
            // reintentar.
            setErrorGuardado(e.message || "No se pudo guardar el plan.");
        } finally {
            setGuardando(false);
        }
    };

    // ------------------------------------------------------------- pantalla
    usePageSubtitulo(seguro ? `${edicion ? "Editar plan" : "Nuevo plan"} · ${seguro.nombre}` : "");

    if (cargandoSeguro || cargando) {
        return (
            <Pagina className="sg-cargando" aria-busy="true">
                Cargando el tarifario…
            </Pagina>
        );
    }

    if (errorSeguro || !seguro) {
        const esRed = errorSeguro === "red";
        return (
            <Pagina>
                <Aviso
                    tipo={esRed ? "error" : "aviso"}
                    accion={
                        esRed
                            ? { label: "Reintentar", onClick: recargar }
                            : { label: "Ir a Seguros", onClick: () => navigate("/configuracion/seguros") }
                    }
                >
                    {esRed
                        ? "No se pudo cargar la aseguradora. Revisa la conexión e inténtalo de nuevo."
                        : "Esta aseguradora no existe o no tienes acceso a ella."}
                </Aviso>
            </Pagina>
        );
    }

    if (errorCarga) {
        return (
            <Pagina>
                <button type="button" className="sg-volver" onClick={volver}>
                    <IconoChevronIzquierda size={15} />
                    Planes de {seguro.nombre}
                </button>
                <Aviso tipo="error" accion={{ label: "Reintentar", onClick: cargar }}>
                    {errorCarga}
                </Aviso>
            </Pagina>
        );
    }

    const puedeGuardar = nombre.trim() && puestos.size > 0 && invalidos.length === 0 && cambios;

    return (
        <Pagina>
            <button type="button" className="sg-volver" onClick={volver} data-testid="pc-volver">
                <IconoChevronIzquierda size={15} />
                Planes de {seguro.nombre}
            </button>

            <div className="pc-barra">
                <div>
                    <h2 className="pc-barra__titulo">
                        {edicion ? `Editar plan${original?.nombre ? `: ${original.nombre}` : ""}` : "Nuevo plan"}
                        {/* No es un estado guardado -- `plan` sólo conoce Activo
                            e Inactivo. Describe el formulario, no el dato. */}
                        {cambios && <ChipEstado tono="pendiente">Sin guardar</ChipEstado>}
                    </h2>
                    <p className="pc-barra__meta">
                        Seguro: {seguro.nombre} · tarifario de {catalogo.length} procedimientos
                    </p>
                </div>
                <div className="pc-barra__acciones">
                    <Boton onClick={() => (cambios ? setConfirmar("salir") : volver())} disabled={guardando}>
                        Cancelar
                    </Boton>
                    <Boton
                        variante="primario"
                        onClick={guardar}
                        cargando={guardando}
                        disabled={!puedeGuardar}
                        data-testid="pc-guardar"
                    >
                        {edicion ? "Guardar cambios" : "Guardar plan"}
                    </Boton>
                </div>
            </div>

            {borradorRecuperado && (
                <Aviso
                    tipo="info"
                    accion={{
                        label: "Descartar borrador",
                        onClick: () => {
                            borrarBorrador(clave);
                            setBorradorRecuperado(false);
                            cargar();
                        },
                    }}
                >
                    Se recuperó un borrador sin guardar de este plan.
                </Aviso>
            )}

            {errorGuardado && (
                <Aviso tipo="error" accion={{ label: "Reintentar", onClick: guardar }}>
                    {errorGuardado} No se ha perdido nada: el plan sigue como lo tenías.
                </Aviso>
            )}

            <div className="pc-datos">
                <div className="pc-datos__nombre">
                    <CampoTexto
                        id={ID_NOMBRE}
                        // Criterio 11: el nombre tiene el foco al abrir. Es lo
                        // que da sentido al trabajo, no un tramite final.
                        autoFocus
                        etiqueta="Nombre del plan"
                        obligatorio
                        placeholder="Por ejemplo: Plan Laboratorio Corporativo"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        data-testid="pc-nombre"
                    />
                </div>
                <div className="pc-datos__copago">
                    <Campo etiqueta="Copago por defecto" ayuda="Se aplica al agregar">
                        <input
                            className="pc-copago"
                            style={{ width: "100%" }}
                            type="number"
                            min={0}
                            max={100}
                            value={copagoDefecto}
                            onChange={(e) => setCopagoDefecto(e.target.value)}
                            data-testid="pc-copago-defecto"
                        />
                    </Campo>
                </div>
                {!edicion && (
                    <div className="pc-datos__duplicar" style={{ width: 250 }}>
                        <CampoSelect
                            etiqueta="¿Parecido a otro plan?"
                            placeholder="Duplicar uno existente"
                            opciones={planesDelSeguro.map((p) => ({
                                value: String(p.id_plan),
                                label: p.nombre,
                            }))}
                            onFocus={cargarPlanesSiHaceFalta}
                            onMouseDown={cargarPlanesSiHaceFalta}
                            onChange={(e) => duplicarDe(e.target.value)}
                            disabled={duplicando}
                            data-testid="pc-duplicar"
                        />
                    </div>
                )}
            </div>

            <div className="pc-paneles">
                {/* ================================================ CATÁLOGO == */}
                <div className="pc-panel">
                    <div className="pc-panel__cabecera">
                        <h3 className="pc-panel__nombre">
                            Tarifario de {seguro.nombre}
                            <span className="sg-tab__contador">{catalogo.length}</span>
                        </h3>
                        <span style={{ fontSize: 12, color: "var(--text-3)" }}>
                            {filtradas.length} resultado{filtradas.length === 1 ? "" : "s"}
                        </span>
                    </div>

                    <div className="pc-panel__controles">
                        <div className="pc-panel__buscador">
                            <CampoTexto
                                etiqueta="Buscar"
                                etiquetaOculta
                                icono={IconoBuscar}
                                placeholder="Buscar por código o descripción"
                                value={busquedaInput}
                                onChange={(e) => setBusquedaInput(e.target.value)}
                                data-testid="pc-buscar"
                            />
                        </div>
                        <div className="pc-panel__categoria">
                            <CampoSelect
                                etiqueta="Categoría"
                                placeholder="Todas las categorías"
                                opciones={categorias}
                                value={categoria}
                                onChange={(e) => setCategoria(e.target.value)}
                                data-testid="pc-categoria"
                            />
                        </div>
                    </div>

                    {/* La franja actúa sobre TODO el filtro, no sobre lo visible.
                        Su texto dice el número exacto, y cambia con los filtros. */}
                    {filtradas.length > 0 && (
                        <div className="pc-franja">
                            <span>
                                {yaPuestasDelFiltro === filtradas.length
                                    ? `Los ${filtradas.length}${nombreCategoria ? ` de ${nombreCategoria}` : ""} ya están en el plan`
                                    : `Seleccionar ${filtradas.length === 1 ? "el" : `los ${filtradas.length}`}${nombreCategoria ? ` de ${nombreCategoria}` : ""}`}
                            </span>
                            {yaPuestasDelFiltro === filtradas.length ? (
                                <Boton tamano="sm" onClick={quitarFiltro} data-testid="pc-quitar-todos">
                                    Quitar los {filtradas.length}
                                </Boton>
                            ) : (
                                <Boton
                                    tamano="sm"
                                    variante="primario"
                                    onClick={agregarFiltro}
                                    data-testid="pc-agregar-todos"
                                >
                                    Agregar los {filtradas.length - yaPuestasDelFiltro}
                                </Boton>
                            )}
                        </div>
                    )}

                    <div className="pc-lista" data-testid="pc-catalogo">
                        {filtradas.length === 0 ? (
                            <p className="sg-vacio">
                                {catalogo.length === 0
                                    ? "Esta aseguradora todavía no tiene tarifario."
                                    : "Ningún procedimiento coincide con el filtro."}
                            </p>
                        ) : (
                            filtradas.map((fila) => {
                                const id = idFila(fila);
                                return (
                                    <Fila
                                        key={id}
                                        fila={fila}
                                        marcada={puestos.has(id)}
                                        bloqueada={Boolean(bloqueadaPor(fila, puestos, ocupados))}
                                        onCambio={alternarFila}
                                    />
                                );
                            })
                        )}
                    </div>

                    <div className="pc-panel__pie">
                        <span>Sin paginación · la selección se guarda por procedimiento</span>
                    </div>
                </div>

                {/* ================================================= EL PLAN == */}
                <div className="pc-panel">
                    <div className="pc-panel__cabecera">
                        <h3 className="pc-panel__nombre">
                            En el plan
                            <span className="sg-tab__contador">{puestos.size}</span>
                        </h3>
                        {puestos.size > 0 && (
                            <Boton tamano="sm" variante="ghost" onClick={() => setConfirmar("vaciar")}>
                                Vaciar
                            </Boton>
                        )}
                    </div>

                    {marcados.size > 0 && (
                        <div className="pc-lote">
                            <span>{marcados.size} seleccionados</span>
                            <label htmlFor="pc-lote-copago">Copago</label>
                            <input
                                id="pc-lote-copago"
                                className="pc-copago"
                                type="number"
                                min={0}
                                max={100}
                                onChange={(e) => copagoEnLote(e.target.value)}
                                data-testid="pc-lote-copago"
                            />
                            <span>%</span>
                            <Boton tamano="sm" variante="destructivo" onClick={quitarEnLote}>
                                Quitar
                            </Boton>
                        </div>
                    )}

                    <div className="pc-lista" data-testid="pc-plan">
                        {puestos.size === 0 ? (
                            <p className="sg-vacio">
                                Todavía no has agregado ningún procedimiento. Márcalos en el tarifario
                                de la izquierda.
                            </p>
                        ) : (
                            grupos.map((grupo) => {
                                const plegado = plegados.has(grupo.catId);
                                const Chevron = plegado ? IconoChevronDerecha : IconoChevronAbajo;
                                return (
                                    <div className="pc-grupo" key={String(grupo.catId)}>
                                        <div className="pc-grupo__cabecera">
                                            <button
                                                type="button"
                                                className="pc-grupo__plegar"
                                                aria-expanded={!plegado}
                                                onClick={() =>
                                                    setPlegados((prev) => {
                                                        const s = new Set(prev);
                                                        if (s.has(grupo.catId)) s.delete(grupo.catId);
                                                        else s.add(grupo.catId);
                                                        return s;
                                                    })
                                                }
                                            >
                                                <Chevron size={15} />
                                                {grupo.etiqueta}
                                                <span className="sg-tab__contador">{grupo.filas.length}</span>
                                            </button>
                                            <span className="pc-grupo__copago">
                                                <label htmlFor={`pc-grupo-${grupo.catId}`}>
                                                    Copago del grupo
                                                </label>
                                                <input
                                                    id={`pc-grupo-${grupo.catId}`}
                                                    className="pc-copago"
                                                    type="number"
                                                    min={0}
                                                    max={100}
                                                    onChange={(e) => copagoDeGrupo(grupo, e.target.value)}
                                                    data-testid={`pc-grupo-copago-${grupo.catId}`}
                                                />
                                            </span>
                                        </div>

                                        {!plegado &&
                                            grupo.filas.map(({ id, fila }) => {
                                                const desc = tituloClinico(
                                                    fila?.ExamenTipo?.descripcion || ""
                                                );
                                                const copago = puestos.get(id);
                                                return (
                                                    <div className="pc-fila" key={id}>
                                                        <Casilla
                                                            marcada={marcados.has(id)}
                                                            etiqueta={`Seleccionar ${desc}`}
                                                            onChange={(v) =>
                                                                setMarcados((prev) => {
                                                                    const s = new Set(prev);
                                                                    if (v) s.add(id);
                                                                    else s.delete(id);
                                                                    return s;
                                                                })
                                                            }
                                                        >
                                                            <span className="pc-fila__texto">
                                                                <span className="pc-fila__desc" title={desc}>
                                                                    {desc}
                                                                </span>
                                                                <span className="pc-fila__codigo">
                                                                    {fila?.ExamenTipo?.codigo || ""}
                                                                </span>
                                                            </span>
                                                            {/* El precio es informativo: se edita en el
                                                                tarifario, nunca desde aquí. */}
                                                            <span className="pc-fila__precio">
                                                                {formatearPrecio(fila?.precio)}
                                                            </span>
                                                        </Casilla>
                                                        <input
                                                            className={`pc-copago${
                                                                copago !== undefined &&
                                                                invalidos.includes(id)
                                                                    ? " pc-copago--invalido"
                                                                    : ""
                                                            }`}
                                                            type="number"
                                                            min={0}
                                                            max={100}
                                                            value={copago ?? ""}
                                                            onChange={(e) => ponerCopago(id, e.target.value)}
                                                            aria-label={`Copago de ${desc}`}
                                                            aria-invalid={invalidos.includes(id)}
                                                            data-testid={`pc-copago-${id}`}
                                                        />
                                                        <Boton
                                                            tamano="sm"
                                                            variante="ghost"
                                                            soloIcono
                                                            icono={IconoEliminar}
                                                            titulo={`Quitar ${desc}`}
                                                            onClick={() => quitarUna(id)}
                                                        />
                                                    </div>
                                                );
                                            })}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="pc-panel__pie">
                        <span>
                            {resumen.procedimientos} procedimiento
                            {resumen.procedimientos === 1 ? "" : "s"}
                            {resumen.copagoPromedio !== null &&
                                ` · Copago promedio ${resumen.copagoPromedio.toFixed(1)} %`}
                            {resumen.categorias > 0 &&
                                ` · ${resumen.categorias} categoría${resumen.categorias === 1 ? "" : "s"}`}
                        </span>
                        {borradorGuardado && <span>Borrador guardado</span>}
                    </div>
                </div>
            </div>

            {/* Trampa 7: un solo diálogo que cambia de contenido, nunca uno
                sobre otro. */}
            <ModalKit
                abierto={confirmar !== null}
                onCerrar={() => setConfirmar(null)}
                titulo={confirmar === "vaciar" ? "Vaciar el plan" : "Salir sin guardar"}
                destructivo
                secundario={{ label: "Cancelar" }}
                primario={{
                    label: confirmar === "vaciar" ? "Vaciar" : "Salir sin guardar",
                    onClick: confirmar === "vaciar" ? vaciar : volver,
                }}
            >
                {confirmar === "vaciar" ? (
                    <p>
                        Se quitarán los {puestos.size} procedimientos del plan. No se guarda nada
                        todavía: puedes volver a agregarlos.
                    </p>
                ) : (
                    <p>
                        Hay cambios sin guardar. Se conservan como borrador en este navegador, así
                        que puedes volver y seguir donde lo dejaste.
                    </p>
                )}
            </ModalKit>
        </Pagina>
    );
}
